"use client";

import posthog from "posthog-js";
import { useEffect } from "react";

import { FormError } from "@/components/forms/form-error";
import { CONTEXT_ISOLATION_ERROR_CODES } from "@/lib/auth/errors";
import { oauthFailureClientContext } from "@/lib/auth/oauth-failure-client-context";
import { isPostHogCapturing } from "@/lib/posthog/is-capturing";

// The #685 context-isolation class (CONTEXT_ISOLATION_ERROR_CODES) gets the
// richer "open in your browser" recovery guidance + the diagnostic probe;
// every other oauth error falls through to the plain banner.
const OAUTH_FAILURE_CONTEXT_EVENT = "oauth_failure_context";

/** localStorage.length, or null when access throws (private mode / partitioned
 *  storage) — itself an isolation signal, but unknowable, so report null. */
function localStorageEmpty(): boolean | null {
  try {
    return window.localStorage.length === 0;
  } catch {
    return null;
  }
}

type Props = { message?: string; code?: string; shell?: boolean };

/** Renders the OAuth-failure banner on `/login?oauth_error=...`. For the PKCE
 *  context-isolation failure it (1) shows actionable in-app-browser recovery
 *  guidance and (2) fires the client-side `oauth_failure_context` probe — the
 *  standalone/referrer/storage signals the server callback can't see, captured
 *  in the broken context itself. Any other oauth error is the plain banner. */
export function OAuthRecoveryNotice({ message, code, shell }: Props) {
  const isContextIsolation = code != null && CONTEXT_ISOLATION_ERROR_CODES.has(code);

  useEffect(() => {
    if (!message || !isContextIsolation || !isPostHogCapturing()) return;
    posthog.capture(
      OAUTH_FAILURE_CONTEXT_EVENT,
      oauthFailureClientContext({
        errorCode: code,
        displayModeStandalone:
          window.matchMedia?.("(display-mode: standalone)").matches ?? null,
        navigatorStandalone:
          (window.navigator as { standalone?: boolean }).standalone ?? null,
        referrer: document.referrer || null,
        localStorageEmpty: localStorageEmpty(),
        cookieEmpty: document.cookie.length === 0,
        userAgent: window.navigator.userAgent,
      }),
    );
  }, [message, isContextIsolation, code]);

  if (!message) return null;
  if (!isContextIsolation) return <FormError message={message} />;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="bg-destructive/10 text-destructive space-y-1.5 rounded-lg px-3 py-2 text-sm"
    >
      <p>{message}</p>
      {shell ? (
        // Native shell: "open medceptor.com in Safari" would strand the
        // session outside the app, so the advice stays in-place.
        //
        // Deliberately names no provider. It once said "try Google again"
        // while the shell offered no third-party sign-in at all, pointing
        // users at a button that wasn't on screen. The shell now offers both
        // Google and Apple (guideline 4.8 — see lib/auth/oauth-availability
        // .ts), and native Apple failures reach this banner too, so the
        // buttons ARE present again — but email/password remains advice that
        // works for every code that lands here. Naming a provider would mean
        // deriving it from the registry, never hardcoding one.
        <p className="text-muted-foreground text-xs">
          Use your email and password below to sign in.
        </p>
      ) : (
        <p className="text-muted-foreground text-xs">
          This can happen when you open Medceptor from inside another app
          (like Gmail or a link preview). Open{" "}
          <span className="text-foreground font-medium">medceptor.com</span>{" "}
          in Safari or Chrome and sign in again, or use your email and
          password below.
        </p>
      )}
    </div>
  );
}
